import { UserInfoFormData } from "./types";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { AUTH_API } from "../../../api/auth";
import AuthButton from "../../../components/AurhButton";
import Input from "../../../components/Input";
import { useAppDispatch } from "../../../hooks/redux-hooks.ts";
import {
  removeProfile,
  setIsNewUser,
  setProfile,
} from "../../../store/slices/profileSlice.ts";
import {
  COOKIE_COMPANY_ID,
  COOKIE_REFRESH_TOKEN_NAME,
  COOKIE_TOKEN_NAME,
  COOKIE_USER_ID,
  setCookie,
} from "../../../utils/cookie.ts";
import { validationSchema } from "./schema.ts";

import { IEmailSignUpParams } from "../../../api/auth/types.ts";
import { LoginStep } from "../Login/types.ts";
import { AuthStepComponentProps } from "../types.ts";

const EmailUserInfo = ({ userData }: AuthStepComponentProps<LoginStep>) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    clearErrors,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserInfoFormData>({
    resolver: yupResolver<UserInfoFormData>(validationSchema),
  });

  const clear = () => {
    dispatch(removeProfile());
  };

  const sendUserInfo = async (payload: IEmailSignUpParams, attempts = 0) => {
    if (userData) {
      setCookie(COOKIE_TOKEN_NAME, userData.token);
      setCookie(COOKIE_REFRESH_TOKEN_NAME, userData.refreshToken);
      setCookie(COOKIE_COMPANY_ID, userData.companyId);
      setCookie(COOKIE_USER_ID, userData.userId);
    }

    const maxRetries = 3;

    if (attempts > maxRetries) {
      clear();
      return;
    }

    try {
      const { data: responseData } = await AUTH_API.emailSignUp(payload);

      if (responseData && userData) {
        dispatch(setIsNewUser(userData?.isNewUser));
        dispatch(setProfile(userData.user));
        navigate("/overview", { replace: true });
        return;
      }
    } catch (error) {
      attempts += 1;
      await sendUserInfo(payload, attempts);
    }
  };

  const onSubmit = async (data: UserInfoFormData) => {
    const payload: IEmailSignUpParams = {
      user: {
        name: data.name,
      },
      company: { name: data.company },
    };

    await sendUserInfo(payload);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="appearAnimation flex flex-col gap-[1.25rem]"
      >
        <Input
          name="name"
          autoFocus
          control={control}
          placeholder="Full name"
          error={errors.name?.message}
          clearErrors={clearErrors}
        />

        <Input
          name="company"
          control={control}
          placeholder="Organization name (optional)"
          error={errors.company?.message}
          clearErrors={clearErrors}
          tooltip={{
            id: "organisation-tooltip",
            content:
              "This will be the name of your account, so choose something you team will recognize.",
          }}
        />

        <AuthButton text={"Agree"} isLoading={isSubmitting} />
      </form>

      <span className="items-center whitespace-pre-line text-center text-[0.875rem] leading-[1.29rem] text-white/50">
        By clicking Agree, you agree to our
        <Link to="/terms" className="text-white underline">
          Terms
        </Link>
        and have read our
        <Link to="/privacy" className="text-white underline">
          Privacy Policy
        </Link>
      </span>
    </>
  );
};

export default EmailUserInfo;
